import React, { useState, useEffect } from 'react';
import axios from 'axios';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

/**
 * Modal de e-mail de negociação com fornecedor.
 * Extraído de Planejamento_orcamentario.js sem mudança visual.
 *
 * Props:
 *  aberto: bool
 *  dadosHistorico: resp.dados de /api/historico-fornecedor/ (fornecedor + historico + totais)
 *  clienteId: id do cliente BPO
 *  nomeRemetente: nome do cliente BPO (assinatura do e-mail)
 *  onFechar: () => void
 *  onMensagem: (texto) => void — toast da página
 *  onEmailDefinido?: (email) => void — chamado quando o usuário informa e-mail novo (p/ salvar em saving-config)
 */
const ModalEmailNegociacao = ({ aberto, dadosHistorico, clienteId, nomeRemetente, onFechar, onMensagem, onEmailDefinido, tipo = 'aumento', valorAdiantadoSugerido = null }) => {
  const [mensagemEmail, setMensagemEmail] = useState('');
  const [analise, setAnalise] = useState(null);
  const [emailDestino, setEmailDestino] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!aberto || !dadosHistorico?.historico) return;

    const hist = [...dadosHistorico.historico].sort((a, b) => a.mes_ano.localeCompare(b.mes_ano));
    const totais = hist.map(m => parseFloat(m.total) || 0);
    const n = totais.length;

    const media = n > 0 ? totais.reduce((s, v) => s + v, 0) / n : 0;
    const valorAtual = totais[n - 1] || 0;

    if (tipo === 'anual') {
      // ── Modo: negociação de contrato anual adiantado ──
      // Meses restantes até dezembro (inclusive o mês atual)
      const mesAtual = new Date().getMonth() + 1;
      const mesesNomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
      const mesesRestantes = Math.max(1, 12 - mesAtual + 1);
      const totalAteDez = media * mesesRestantes;          // total se mantiver o pagamento mensal
      // Usa o valor que o usuário digitou na esteira (se houver); senão sugere ~10% de desconto
      const valorSugeridoAntecipado = (valorAdiantadoSugerido && valorAdiantadoSugerido > 0)
        ? valorAdiantadoSugerido
        : totalAteDez * 0.90;
      const economiaSugerida = totalAteDez - valorSugeridoAntecipado;
      const pctDesconto = totalAteDez > 0 ? (economiaSugerida / totalAteDez) * 100 : 0;

      const textoAnual = `Prezado(a) ${dadosHistorico.fornecedor.nome},

Espero que esta mensagem o(a) encontre bem.

Temos um relacionamento recorrente e atualmente realizamos o pagamento mensal de aproximadamente ${formatarValor(media)}.

PROPOSTA DE PAGAMENTO ANTECIPADO ATÉ DEZEMBRO

Gostaríamos de avaliar a possibilidade de quitar de forma antecipada os ${mesesRestantes} ${mesesRestantes === 1 ? 'mês' : 'meses'} restantes do ano (de ${mesesNomes[mesAtual - 1]} a dezembro), em troca de uma condição comercial diferenciada (desconto).

- Valor total se mantido o pagamento mês a mês até dezembro: ${formatarValor(totalAteDez)}
- Valor proposto para o pagamento antecipado${pctDesconto > 0 ? ` (desconto de ${pctDesconto.toFixed(1)}%)` : ''}: ${formatarValor(valorSugeridoAntecipado)}

Considerando esses números, qual seria a melhor condição que vocês conseguem oferecer para o adiantamento?

PRÓXIMOS PASSOS

Ficamos à disposição para alinhar formato, prazo e o desconto do adiantamento, buscando a melhor condição para ambas as partes.

Atenciosamente,
${nomeRemetente || ''}`;

      setMensagemEmail(textoAnual);
      setEmailDestino(dadosHistorico.fornecedor.email || '');
      setAnalise({
        tipo: 'anual',
        media,
        valorAnualCheio: totalAteDez,
        mesesRestantes,
        valorSugeridoAntecipado,
        valorAtual,
        totalPago: dadosHistorico.total_geral,
        meses: n,
        economia: economiaSugerida,
        valorSugerido: valorSugeridoAntecipado,
      });
      setEnviado(false);
      return;
    }

    if (tipo === 'explicacao') {
      // ── Modo: avisar sobre o aumento e pedir explicação ──
      const u3 = totais.slice(-Math.min(3, n));
      const mediaRec = u3.reduce((s, v) => s + v, 0) / u3.length;
      const p3 = totais.slice(0, Math.min(3, n));
      const mediaB = p3.reduce((s, v) => s + v, 0) / p3.length;
      const tend = n >= 3 && mediaB > 0 ? ((mediaRec - mediaB) / mediaB) * 100 : null;
      const variacaoUltimo = media > 0 ? ((valorAtual - media) / media) * 100 : null;
      const pctAumento = tend !== null ? tend : variacaoUltimo;

      const textoExpl = `Prezado(a) ${dadosHistorico.fornecedor.nome},

Espero que esta mensagem o(a) encontre bem.

Ao revisar o histórico de pagamentos referente aos serviços/produtos fornecidos, identificamos um aumento nos valores praticados recentemente:

- Média mensal histórica: ${formatarValor(media)}
- Último pagamento: ${formatarValor(valorAtual)}${pctAumento !== null && pctAumento > 0 ? `\n- Aumento identificado: ${pctAumento.toFixed(1)}%` : ''}

Para mantermos uma parceria transparente e o nosso planejamento financeiro em dia, gostaríamos de entender melhor os motivos desse reajuste.

Poderia, por gentileza, nos encaminhar uma explicação sobre o aumento aplicado — por exemplo, a composição de custos, o índice de reajuste utilizado e o período de vigência?

Ficamos no aguardo do seu retorno e à disposição para conversar.

Atenciosamente,
${nomeRemetente || ''}`;

      setMensagemEmail(textoExpl);
      setEmailDestino(dadosHistorico.fornecedor.email || '');
      setAnalise({
        tipo: 'explicacao',
        media,
        valorAtual,
        tendencia: pctAumento,
        totalPago: dadosHistorico.total_geral,
        meses: n,
        economia: 0,
        valorSugerido: 0,
      });
      setEnviado(false);
      return;
    }

    const ultimos3 = totais.slice(-Math.min(3, n));
    const mediaRecente = ultimos3.reduce((s, v) => s + v, 0) / ultimos3.length;

    const primeiros3 = totais.slice(0, Math.min(3, n));
    const mediaBase = primeiros3.reduce((s, v) => s + v, 0) / primeiros3.length;
    const tendencia = n >= 3 && mediaBase > 0 ? ((mediaRecente - mediaBase) / mediaBase) * 100 : null;

    let valorSugerido, justificativaAuto;
    if (tendencia === null) {
      valorSugerido = media;
      justificativaAuto = `Com base em ${n} mês${n > 1 ? 'es' : ''} de histórico, sugerimos manter a média de ${formatarValor(media)} como referência para renovação do contrato.`;
    } else if (tendencia > 10) {
      valorSugerido = media * 0.95;
      justificativaAuto = `Identificamos um aumento de ${tendencia.toFixed(1)}% nos pagamentos ao longo do período. Sugerimos negociar uma redução de aproximadamente 5% sobre a média histórica (${formatarValor(media)}), resultando em ${formatarValor(valorSugerido)} como valor de referência.`;
    } else if (tendencia < -10) {
      valorSugerido = mediaRecente;
      justificativaAuto = `Os valores apresentaram redução de ${Math.abs(tendencia).toFixed(1)}% ao longo do tempo, o que é positivo. Sugerimos manter a média dos últimos 3 meses (${formatarValor(mediaRecente)}) como referência.`;
    } else {
      valorSugerido = media;
      justificativaAuto = `Os valores estão estáveis (variação de ${Math.abs(tendencia).toFixed(1)}%). Sugerimos usar a média histórica de ${formatarValor(media)} como base para renovação.`;
    }

    const economia = (valorAtual - valorSugerido) * 12;

    const textoInicial = `Prezado(a) ${dadosHistorico.fornecedor.nome},

Espero que esta mensagem o(a) encontre bem.

Estamos realizando uma revisão estratégica dos contratos e valores praticados com nossos fornecedores, e gostaríamos de iniciar uma conversa sobre a possibilidade de otimização dos custos relacionados aos serviços prestados.

Demonstrativo de pagamentos

Com base no histórico dos últimos ${n} ${n === 1 ? 'mês' : 'meses'}, identificamos os seguintes dados:

- Total pago no período: ${formatarValor(dadosHistorico.total_geral)}
- Média mensal: ${formatarValor(media)}
- Último pagamento: ${formatarValor(valorAtual)}
${tendencia !== null ? `• Tendência: ${tendencia > 0 ? 'Aumento' : 'Redução'} de ${Math.abs(tendencia).toFixed(1)}%` : ''}

PROPOSTA DE NOVA NEGOCIAÇÃO

${justificativaAuto}

- Valor sugerido por mês: ${formatarValor(valorSugerido)}

${economia > 0 ? `Essa otimização representaria uma economia anual estimada de ${formatarValor(economia)}, contribuindo significativamente para a sustentabilidade financeira de ambas as partes.` : ''}

PRÓXIMOS PASSOS

Gostaríamos de agendar uma reunião para discutir essa proposta com mais detalhes, sempre buscando uma solução que seja vantajosa para ambos.

Ficamos à disposição para alinhar os próximos passos.

Atenciosamente,
${nomeRemetente || ''}`;

    setMensagemEmail(textoInicial);
    setEmailDestino(dadosHistorico.fornecedor.email || '');
    setAnalise({
      tipo: 'aumento',
      valorSugerido,
      economia,
      media,
      valorAtual,
      totalPago: dadosHistorico.total_geral,
      meses: n,
    });
    setEnviado(false);
  }, [aberto, dadosHistorico, nomeRemetente, tipo, valorAdiantadoSugerido]);

  if (!aberto || !dadosHistorico || !analise) return null;

  const fornecedor = dadosHistorico.fornecedor;
  const emailOriginalVazio = !fornecedor.email;

  const enviarEmail = async () => {
    setEnviando(true);
    try {
      const token = localStorage.getItem('access');

      if (emailOriginalVazio && emailDestino && onEmailDefinido) {
        onEmailDefinido(emailDestino);
      }

      const payload = {
        cliente_id: clienteId,
        codigo_fornecedor: fornecedor.codigo,
        email_fornecedor: emailDestino,
        nome_fornecedor: fornecedor.nome,
        cnpj_cpf: fornecedor.cnpj_cpf,
        historico: dadosHistorico.historico,
        total_geral: dadosHistorico.total_geral,
        total_meses: dadosHistorico.total_meses,
        mensagem: mensagemEmail,
        tipo_saving: tipo === 'anual' ? 'anual' : 'aumento',
        dados_analise: {
          valorSugerido: analise.valorSugerido,
          economia: analise.economia,
          media: analise.media,
          valorAtual: analise.valorAtual,
          totalPago: analise.totalPago,
          meses: analise.meses,
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/enviar-email-negociacao-fornecedor/`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setEnviado(true);
        onMensagem && onMensagem('Email enviado com sucesso para o fornecedor!');
        setTimeout(() => {
          onFechar();
          onMensagem && onMensagem('');
        }, 3000);
      }
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      onMensagem && onMensagem('Erro ao enviar email. Verifique as configurações.');
      setTimeout(() => onMensagem && onMensagem(''), 5000);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1090 }}
      onClick={() => !enviando && onFechar()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-handshake me-2" style={{ color: '#212529' }}></i>
                {tipo === 'explicacao' ? 'Pedido de Explicação do Aumento' : tipo === 'anual' ? 'Proposta de Contrato Anual' : 'Email de Negociação'} - {fornecedor.nome}
              </h5>
              <small style={{ color: '#6c757d' }}>
                {fornecedor.cnpj_cpf && `${fornecedor.cnpj_cpf} • `}
                {emailDestino || 'e-mail não cadastrado'}
              </small>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onFechar}
              disabled={enviando}
            ></button>
          </div>

          <div className="modal-body">
            {enviado ? (
              <div className="text-center py-5">
                <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                <h4 className="text-success">Email Enviado com Sucesso!</h4>
                <p className="text-muted">
                  O email de negociação foi enviado para <strong>{emailDestino}</strong>
                </p>
              </div>
            ) : (
              <>
                <div style={{ margin: '0 0 20px', borderRadius: '10px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
                  <div style={{ backgroundColor: '#212529', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="fas fa-robot" style={{ color: '#f39c12', fontSize: '18px' }}></i>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>
                      Análise Inteligente do Contrato
                    </span>
                    <span style={{ color: '#adb5bd', fontSize: '12px' }}>
                      — {analise.meses} {analise.meses === 1 ? 'mês' : 'meses'} de histórico
                    </span>
                  </div>

                  <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', backgroundColor: '#fff' }}>
                    {(analise.tipo === 'anual'
                      ? [
                          { label: 'Média Mensal', valor: formatarValor(analise.media), cor: '#2980b9' },
                          { label: `Total até dez. (${analise.mesesRestantes}x)`, valor: formatarValor(analise.valorAnualCheio), cor: '#8e44ad' },
                          { label: 'Sugerido antecipado', valor: formatarValor(analise.valorSugeridoAntecipado), cor: '#27ae60' },
                        ]
                      : analise.tipo === 'explicacao'
                      ? [
                          { label: 'Média Mensal', valor: formatarValor(analise.media), cor: '#2980b9' },
                          { label: 'Último Pagamento', valor: formatarValor(analise.valorAtual), cor: '#8e44ad' },
                          { label: 'Aumento', valor: (analise.tendencia !== null && analise.tendencia !== undefined) ? `${analise.tendencia > 0 ? '+' : ''}${analise.tendencia.toFixed(1)}%` : '—', cor: '#e74c3c' },
                        ]
                      : [
                          { label: 'Total Pago', valor: formatarValor(analise.totalPago), cor: '#e74c3c' },
                          { label: 'Média Mensal', valor: formatarValor(analise.media), cor: '#2980b9' },
                          { label: 'Último Pagamento', valor: formatarValor(analise.valorAtual), cor: '#8e44ad' },
                          { label: 'Valor Sugerido', valor: formatarValor(analise.valorSugerido), cor: '#27ae60' },
                        ]
                    ).map((item, i, arr) => (
                      <div
                        key={i}
                        style={{ flex: 1, textAlign: 'center', padding: '14px 12px', borderRight: i < arr.length - 1 ? '1px solid #dee2e6' : 'none' }}
                      >
                        <div style={{ color: '#6c757d', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>
                          {item.label}
                        </div>
                        <div style={{ color: item.cor, fontWeight: 700, fontSize: '16px' }}>
                          {item.valor}
                        </div>
                      </div>
                    ))}
                  </div>

                  {analise.economia > 0 && (
                    <div style={{ backgroundColor: '#d4edda', padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ color: '#155724', fontSize: '13px', marginBottom: '4px' }}>
                        Economia Anual Estimada
                      </div>
                      <div style={{ color: '#155724', fontWeight: 700, fontSize: '20px' }}>
                        {formatarValor(analise.economia)}
                      </div>
                    </div>
                  )}
                </div>

                {emailOriginalVazio && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-at me-2 text-warning"></i>
                      E-mail do Fornecedor
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={emailDestino}
                      onChange={(e) => setEmailDestino(e.target.value)}
                      placeholder="fornecedor@empresa.com.br"
                    />
                    <small className="text-muted">
                      Este fornecedor não tem e-mail cadastrado.
                      {onEmailDefinido ? ' O endereço informado será salvo para os próximos envios.' : ''}
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="fas fa-edit me-2 text-primary"></i>
                    Mensagem para o Fornecedor
                  </label>
                  <textarea
                    className="form-control"
                    rows="20"
                    value={mensagemEmail}
                    onChange={(e) => setMensagemEmail(e.target.value)}
                    placeholder="Digite aqui a mensagem de negociação..."
                    style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.6' }}
                  />
                  <small className="text-muted">
                    <i className="fas fa-lightbulb me-1"></i>
                    Personalize a mensagem conforme necessário. Os dados da análise já estão incluídos.
                  </small>
                </div>

                <div className="alert alert-warning border-start border-4 mb-0" style={{ borderColor: '#f39c12' }}>
                  <small>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Atenção:</strong> O email incluirá uma tabela detalhada com todo o histórico de pagamentos mês a mês.
                  </small>
                </div>
              </>
            )}
          </div>

          {!enviado && (
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={onFechar}
                disabled={enviando}
              >
                <i className="fas fa-times me-2"></i>
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={enviarEmail}
                disabled={enviando || !mensagemEmail.trim() || !emailDestino.trim()}
              >
                {enviando ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalEmailNegociacao;
